import { useState, useRef } from "react";
import { ApiClient } from "@chefaiq/shared/lib/api-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bot, Database, BarChart3, Loader2, Upload, BookOpen } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { queryKeys } from "@/lib/queryKeys";
import { KBList } from "@/components/knowledge-base/KBList";

const GRID_COLS: Record<number, string> = { 3: "md:grid-cols-3", 4: "md:grid-cols-4", 5: "md:grid-cols-5" };

function StatCards({ stats, cols = 4 }: { stats: { label: string; value?: number }[]; cols?: number }) {
  return (
    <div className={`grid gap-4 ${GRID_COLS[cols] || GRID_COLS[4]}`}>
      {stats.map(s => (
        <Card key={s.label}>
          <CardHeader className="pb-3">
            <CardDescription>{s.label}</CardDescription>
            <CardTitle className="text-3xl">{s.value?.toLocaleString() || 0}</CardTitle>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}

function LoadingOrContent({ isLoading, children }: { isLoading: boolean; children: React.ReactNode }) {
  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  return <>{children}</>;
}

function EmptyList({ message }: { message: string }) {
  return <p className="p-8 text-center text-muted-foreground">{message}</p>;
}

export default function AdminAIManagement() {
  const [systemPrompt, setSystemPrompt] = useState("");
  const { currentWorkspace } = useWorkspace();

  const { data: promptData } = useQuery<any>({
    queryKey: ["/api/ai-usage/prompt"],
    queryFn: () => apiRequest("GET", "/api/ai-usage/prompt")
  });

  if (promptData?.prompt && !systemPrompt) {
    setSystemPrompt(promptData.prompt);
  }

  const updatePromptMutation = useApiMutation({
    endpoint: "/api/ai-usage/prompt",
    method: "PUT",
    invalidate: [["/api/ai-usage/prompt"]],
    successMessage: "System prompt updated successfully",
  });

  const { data: usageData, isLoading: usageLoading } = useQuery<any>({
    queryKey: ["/api/ai-usage"],
    queryFn: () => apiRequest("GET", "/api/ai-usage"),
  });

  const toggleAIMutation = useApiMutation({
    endpoint: (vars: { workspaceId: string }) => `/api/workspaces/${vars.workspaceId}`,
    method: "PATCH",
    invalidate: [queryKeys.workspaces.all()],
    errorMessage: "Failed to update",
    successMessage: "AI settings updated",
  });

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div className="container mx-auto py-6 max-w-6xl space-y-6">
      <div>
        <h1 className="text-xl sm:text-3xl font-bold">AI Management</h1>
        <p className="text-muted-foreground">Manage AI knowledge base, assistant settings, and usage</p>
      </div>

      <Tabs defaultValue="knowledge-base">
        <TabsList>
          <TabsTrigger value="knowledge-base" className="flex items-center gap-2"><Database className="h-4 w-4" />Knowledge Base</TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2"><Bot className="h-4 w-4" />AI Settings</TabsTrigger>
          <TabsTrigger value="usage" className="flex items-center gap-2"><BarChart3 className="h-4 w-4" />Usage Report</TabsTrigger>
          <TabsTrigger value="library-import" className="flex items-center gap-2"><BookOpen className="h-4 w-4" />Library Import</TabsTrigger>
        </TabsList>

        <TabsContent value="knowledge-base" className="space-y-6">
          <KBList />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>AI Assistant Control</CardTitle>
              <CardDescription>Enable or disable AI assistant for all users</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="ai-enabled">Enable AI Assistant</Label>
                    <p className="text-sm text-muted-foreground">When disabled, AI assistant will be hidden for all users</p>
                  </div>
                  <Switch
                    id="ai-enabled"
                    checked={(currentWorkspace as any)?.aiChatEnabled ?? true}
                    onCheckedChange={(checked) => toggleAIMutation.mutate({ workspaceId: currentWorkspace?._id, aiChatEnabled: checked } as any)}
                    disabled={toggleAIMutation.isPending}
                  />
                </div>
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <p className="text-sm font-medium">How AI Access Works:</p>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>AI must be enabled here (admin control)</li>
                    <li>Users need AI permission in their subscription plan</li>
                    <li>Both conditions must be true for users to see the AI assistant</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>System Prompt</CardTitle>
              <CardDescription>Customize how the AI assistant responds to users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} rows={12} className="font-mono text-sm" placeholder="Enter system prompt..." />
              <Button onClick={() => updatePromptMutation.mutate({ prompt: systemPrompt })} disabled={updatePromptMutation.isPending}>
                {updatePromptMutation.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : "Save Prompt"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <LoadingOrContent isLoading={usageLoading}>
            {/* Chat Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Chat Usage Statistics</CardTitle>
                <CardDescription>AI assistant chat usage across your workspace</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <StatCards stats={[
                  { label: "Total Messages", value: usageData?.chat?.totals?.totalMessages },
                  { label: "Chat Tokens", value: usageData?.chat?.totals?.totalTokens },
                  { label: "Characters", value: usageData?.chat?.totals?.totalCharacters },
                ]} cols={3} />

                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50"><h3 className="font-semibold">Chat Usage by User</h3></div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {usageData?.chat?.byUser?.length > 0 ? usageData.chat.byUser.map((u: any) => (
                      <div key={u._id} className="p-4 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{u.userName}</p>
                          <p className="text-sm text-muted-foreground">{u.userEmail}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{u.messageCount} msgs • {u.tokenCount?.toLocaleString()} tokens</p>
                          <p className="text-xs text-muted-foreground">{u.characterCount?.toLocaleString()} chars</p>
                        </div>
                      </div>
                    )) : <EmptyList message="No chat activity yet" />}
                  </div>
                </div>

                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50"><h3 className="font-semibold">Monthly Chat Summary</h3></div>
                  <div className="divide-y">
                    {usageData?.chat?.byMonth?.length > 0 ? usageData.chat.byMonth.map((m: any, i: number) => (
                      <div key={i} className="p-4 flex justify-between items-center">
                        <p className="font-medium">{monthNames[m._id.month - 1]} {m._id.year}</p>
                        <div className="text-right text-sm">
                          <p>{m.messages} msgs • {m.tokens?.toLocaleString()} tokens • {m.characters?.toLocaleString()} chars</p>
                        </div>
                      </div>
                    )) : <EmptyList message="No monthly data yet" />}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Import Token Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Recipe Import Token Usage</CardTitle>
                <CardDescription>AI tokens used for PDF and website recipe extraction</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <StatCards cols={3} stats={[
                  { label: "Total Tokens", value: usageData?.imports?.totals?.total_tokens },
                  { label: "Input Tokens", value: usageData?.imports?.totals?.input_tokens },
                  { label: "Output Tokens", value: usageData?.imports?.totals?.output_tokens },
                ]} />

                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50"><h3 className="font-semibold">Import Usage by User</h3></div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {usageData?.imports?.byUser?.length > 0 ? usageData.imports.byUser.map((u: any) => (
                      <div key={u._id} className="p-4 flex justify-between items-center">
                        <div>
                          <p className="font-medium">{u.userName || 'Unknown User'}</p>
                          <p className="text-sm text-muted-foreground">{u.userEmail || 'N/A'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{u.importCount} imports • {u.totalTokens?.toLocaleString()} tokens</p>
                          <p className="text-xs text-muted-foreground">In: {u.inputTokens?.toLocaleString()} | Out: {u.outputTokens?.toLocaleString()}</p>
                        </div>
                      </div>
                    )) : <EmptyList message="No import activity yet" />}
                  </div>
                </div>

                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50"><h3 className="font-semibold">Monthly Import Summary</h3></div>
                  <div className="divide-y">
                    {usageData?.imports?.byMonth?.length > 0 ? usageData.imports.byMonth.map((m: any, i: number) => (
                      <div key={i} className="p-4 flex justify-between items-center">
                        <p className="font-medium">{monthNames[m._id.month - 1]} {m._id.year}</p>
                        <p className="text-sm">{m.imports} imports • {m.tokens?.toLocaleString()} tokens</p>
                      </div>
                    )) : <EmptyList message="No monthly import data yet" />}
                  </div>
                </div>

                <div className="border rounded-lg">
                  <div className="p-4 border-b bg-muted/50"><h3 className="font-semibold">Recent Imports</h3></div>
                  <div className="divide-y max-h-96 overflow-y-auto">
                    {usageData?.imports?.recentLogs?.length > 0 ? usageData.imports.recentLogs.map((log: any) => (
                      <div key={log._id} className="p-4 hover:bg-muted/50">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{log.recipeName || 'Unknown Recipe'}</p>
                            <p className="text-sm text-muted-foreground">{log.source === 'website_import' ? 'Website Import' : 'PDF Import'}</p>
                            <p className="text-xs text-muted-foreground mt-1">{new Date(log.timestamp).toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{log.total_tokens?.toLocaleString()} tokens</p>
                            <p className="text-xs text-muted-foreground">In: {log.input_tokens?.toLocaleString()} | Out: {log.output_tokens?.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                    )) : <EmptyList message="No recent imports" />}
                  </div>
                </div>
              </CardContent>
            </Card>
          </LoadingOrContent>
        </TabsContent>

        <TabsContent value="library-import" className="space-y-6">
          <LibraryImportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LibraryImportTab() {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [target, setTarget] = useState<'chefaiq' | 'tba'>('chefaiq');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleImport = async () => {
    if (!file) return toast({ title: 'Select a file first', variant: 'destructive' });
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target', target);
      const res = await fetch('/api/admin/library-import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${ApiClient.getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setResult(data);
      toast({ title: `Recipe "${data.name}" imported to ${target === 'chefaiq' ? 'ChefAIQ' : 'TBA'} library` });
      setFile(null);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Import Recipe to Library</CardTitle>
        <CardDescription>Upload a PDF or image — AI extracts the recipe and saves it to the selected library with ingredients, materials, and equipment mapped to starter collections.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label>Target Library</Label>
          <Select value={target} onValueChange={(v: any) => setTarget(v)}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="chefaiq">ChefAIQ Library</SelectItem>
              <SelectItem value="tba">TBA Library</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label>Recipe File (PDF or Image)</Label>
          <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} />
          {file ? (
            <div className="flex items-center gap-2 p-2 border rounded text-sm">
              <Upload className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => setFile(null)}>Remove</Button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
              onDragLeave={e => e.currentTarget.classList.remove('border-primary')}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('border-primary'); setFile(e.dataTransfer.files?.[0] || null); }}
            >
              <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Drag & drop or <span className="text-primary">browse</span></p>
              <p className="text-xs text-muted-foreground mt-1">PDF, Image, Word</p>
            </div>
          )}
        </div>

        <Button onClick={handleImport} disabled={loading || !file}>
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing...</> : <><BookOpen className="mr-2 h-4 w-4" />Import to Library</>}
        </Button>

        {result && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
            <p className="font-medium text-green-800">✅ Imported: {result.name}</p>
            <p className="text-green-700 text-xs mt-1">Saved to {result.target === 'chefaiq' ? 'ChefAIQ' : 'TBA'} library · ID: {result.id}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
